"use client";

import { useRouter } from "next/navigation";
import Link from "next/link";
import { useCampaign } from "@/lib/campaign-context";
import {
  Rocket,
  Settings,
  FileText,
  ArrowRight,
  Globe,
  BookOpen,
  ImageIcon,
  PenTool,
  Video,
  Send,
  BarChart3,
} from "lucide-react";

const features = [
  {
    icon: Globe,
    title: "Landing Page Analysis",
    description:
      "Automatically scrape and analyze affiliate landing pages to extract key selling points and audience insights.",
  },
  {
    icon: BookOpen,
    title: "Magic Seven Research",
    description:
      "AI-powered research using the Magic Seven framework to uncover deep audience motivations and pain points.",
  },
  {
    icon: ImageIcon,
    title: "AI Image Generation",
    description:
      "Generate scroll-stopping ad creatives with fal.ai, tailored to your offer and target audience.",
  },
  {
    icon: PenTool,
    title: "Dan Kennedy Ad Copy",
    description:
      "Craft high-converting ad copy using Dan Kennedy's direct response principles, optimized for Facebook.",
  },
  {
    icon: Video,
    title: "UGC Avatar Video",
    description:
      "Create authentic UGC-style video ads with HeyGen AI avatars that build trust and drive clicks.",
  },
  {
    icon: Globe,
    title: "Landing Page Builder",
    description:
      "Auto-generate high-converting bridge pages using your campaign data and direct response copywriting principles.",
  },
  {
    icon: Send,
    title: "One-Click Meta Publishing",
    description:
      "Publish your completed ad campaigns directly to Meta Ads Manager with a single click.",
  },
];

const pipelineSteps = [
  "Research",
  "Concepts",
  "Copy",
  "Video",
  "Landing Page",
  "Publish",
];

export default function Home() {
  const router = useRouter();
  const { resetCampaign } = useCampaign();

  function handleNewCampaign() {
    resetCampaign();
    router.push("/campaign");
  }

  return (
    <div className="flex flex-col flex-1">
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-6 pt-24 pb-16 text-center">
        <h1 className="text-5xl font-extrabold tracking-tight sm:text-7xl">
          <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
            AdForge
          </span>
        </h1>
        <p className="mt-4 max-w-xl text-lg text-gray-400 sm:text-xl">
          AI-Powered Affiliate Ad Campaigns for Facebook
        </p>

        {/* Pipeline steps */}
        <div className="mt-10 flex items-center gap-2 text-sm font-medium text-gray-500">
          {pipelineSteps.map((step, i) => (
            <span key={step} className="flex items-center gap-2">
              <span className="rounded-full bg-gray-800 px-3 py-1 text-gray-300">
                {step}
              </span>
              {i < pipelineSteps.length - 1 && (
                <ArrowRight className="h-4 w-4 text-gray-600" />
              )}
            </span>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <button
            onClick={handleNewCampaign}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-500"
          >
            <Rocket className="h-5 w-5" />
            New Campaign
          </button>
          <Link
            href="/drafts"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-6 py-3 font-semibold text-gray-300 hover:border-gray-500 hover:text-white"
          >
            <FileText className="h-5 w-5" />
            Drafts
          </Link>
          <Link
            href="/tracking"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-6 py-3 font-semibold text-gray-300 hover:border-gray-500 hover:text-white"
          >
            <BarChart3 className="h-5 w-5" />
            Tracking
          </Link>
          <Link
            href="/settings"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-6 py-3 font-semibold text-gray-300 hover:border-gray-500 hover:text-white"
          >
            <Settings className="h-5 w-5" />
            Settings
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto w-full max-w-6xl px-6 pb-24">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-xl border border-gray-800 bg-gray-900 p-6 hover:border-gray-700"
            >
              <feature.icon className="mb-4 h-8 w-8 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
